import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import PageAccessRoute from '@/components/PageAccessRoute';
import { Toaster } from '@/components/ui/sonner';
import ErrorBoundary from '@/components/ErrorBoundary';
import DebugPanel from '@/components/DebugPanel';
import { initGlobalErrorHandlers } from '@/utils/debugLogger';
import '@/App.css';

// Pages
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DashboardPage from '@/pages/DashboardPage';
import ProjectsPage from '@/pages/ProjectsPage';
import ProjectsOverviewPage from '@/pages/ProjectsOverviewPage';
import ProjectDetailPage from '@/pages/ProjectDetailPage';
import EmployeesPage from '@/pages/EmployeesPage';
import ReportBuilderPage from '@/pages/ReportBuilderPage';
import ProjectGanttPage from '@/pages/ProjectGanttPage';
import BitrixIntegrationPage from '@/pages/BitrixIntegrationPage';
import FieldRegistryPage from '@/pages/FieldRegistryPage';
import AgentDashboardPage from '@/pages/AgentDashboardPage';
import GoszakupPortalPage from '@/pages/GoszakupPortalPage';

// Protected Route Component - базовая проверка авторизации
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Загрузка...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Goszakup Route - изолированная роль, только GoszakupPortalPage
const GoszakupRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Загрузка...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Роль goszakup видит только свою страницу
  if (user.role?.toLowerCase() === 'goszakup') {
    return <GoszakupPortalPage />;
  }

  // Остальные роли - редирект на dashboard
  return <Navigate to="/dashboard" replace />;
};

// Root Redirect - перенаправление на правильную страницу в зависимости от роли
const RootRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Загрузка...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Goszakup -> изолированная страница
  if (user.role?.toLowerCase() === 'goszakup') {
    return <Navigate to="/goszakup" replace />;
  }

  // Остальные -> dashboard
  return <Navigate to="/dashboard" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Dashboard - проверка через матрицу доступа */}
      <Route
        path="/dashboard"
        element={
          <PageAccessRoute>
            <DashboardPage />
          </PageAccessRoute>
        }
      />

      {/* Projects Overview - проверка через матрицу доступа */}
      <Route
        path="/projects"
        element={
          <PageAccessRoute>
            <ProjectsOverviewPage />
          </PageAccessRoute>
        }
      />

      {/* Projects Legacy List */}
      <Route
        path="/projects/list"
        element={
          <PageAccessRoute>
            <ProjectsPage key="projects-list" />
          </PageAccessRoute>
        }
      />

      {/* Create New Project - ВАЖНО: static route ВЫШЕ dynamic :id */}
      <Route
        path="/projects/new"
        element={
          <PageAccessRoute>
            <ProjectsPage createMode={true} key="projects-new" />
          </PageAccessRoute>
        }
      />

      {/* Project Detail - dynamic route ПОСЛЕ static routes */}
      <Route
        path="/projects/:id"
        element={
          <PageAccessRoute>
            <ProjectDetailPage />
          </PageAccessRoute>
        }
      />

      {/* Gantt */}
      <Route
        path="/projects/:id/gantt"
        element={
          <PageAccessRoute>
            <ProjectGanttPage />
          </PageAccessRoute>
        }
      />

      {/* Employees */}
      <Route
        path="/employees"
        element={
          <PageAccessRoute>
            <EmployeesPage />
          </PageAccessRoute>
        }
      />

      {/* Report Builder */}
      <Route
        path="/report-builder"
        element={
          <PageAccessRoute>
            <ReportBuilderPage />
          </PageAccessRoute>
        }
      />

      {/* Bitrix Integration */}
      <Route
        path="/integrations/bitrix"
        element={
          <PageAccessRoute>
            <BitrixIntegrationPage />
          </PageAccessRoute>
        }
      />

      {/* Field Registry */}
      <Route
        path="/admin/field-registry"
        element={
          <PageAccessRoute>
            <FieldRegistryPage />
          </PageAccessRoute>
        }
      />

      {/* Agent Dashboard - personal page for o.antsiferov@logitex.kz only */}
      <Route
        path="/me/agent"
        element={
          <ProtectedRoute>
            <AgentDashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Goszakup Portal - изолированная роль, только кнопка Procurement Intel */}
      <Route path="/goszakup" element={<GoszakupRoute />} />

      <Route path="/" element={<RootRedirect />} />
    </Routes>
  );
}

function App() {
  // Инициализируем глобальные обработчики ошибок при старте
  useEffect(() => {
    initGlobalErrorHandlers();
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
          <DebugPanel />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
